import { useState, useCallback } from 'react';
import { ZodError, type ZodType } from 'zod';
import {
  UpdateEnrollmentProgressInputSchema,
  UpsertEnrollmentStepDataInputSchema,
  UpdateMedicalRecordInputSchema,
  CompleteEnrollmentInputSchema,
  type UpdateEnrollmentProgressInput,
  type UpsertEnrollmentStepDataInput,
  type UpdateMedicalRecordInput,
  type CompleteEnrollmentInput,
  type ChildApplication,
  type Application
} from '@triedge/admissions-domain';
import {
  completeEnrollment as completeEnrollmentApi,
  upsertEnrollmentStepData as upsertEnrollmentStepDataApi,
  updateEnrollmentProgress as updateEnrollmentProgressApi,
  updateMedicalRecord as updateMedicalRecordApi,
} from '../api/admissions';
import { isAmplifyConfigured } from '../adapters/amplify-client';

export class EnrollmentValidationError extends Error {
  constructor(
    message: string,
    public readonly zodError: ZodError,
    public readonly fieldErrors: Record<string, string[]>
  ) {
    super(message);
    this.name = 'EnrollmentValidationError';
  }
}

export class EnrollmentSubmissionError extends Error {
  constructor(
    message: string,
    public readonly code: string,
    public readonly originalError?: unknown
  ) {
    super(message);
    this.name = 'EnrollmentSubmissionError';
  }
}

export interface EnrollmentProgressState {
  updating: boolean;
  error: EnrollmentValidationError | EnrollmentSubmissionError | null;
  lastResult: ChildApplication | Application | null;
  updateProgress: (data: unknown) => Promise<ChildApplication>;
  upsertStepData: (data: unknown) => Promise<ChildApplication>;
  updateMedicalRecord: (data: unknown) => Promise<ChildApplication>;
  completeEnrollment: (data: unknown) => Promise<Application>;
  reset: () => void;
}

function parseInput<T>(schema: ZodType<T>, data: unknown): T {
  const parseResult = schema.safeParse(data);
  if (!parseResult.success) {
    const fieldErrors: Record<string, string[]> = {};
    for (const issue of parseResult.error.issues) {
      const path = issue.path.join('.');
      if (!fieldErrors[path]) {
        fieldErrors[path] = [];
      }
      fieldErrors[path].push(issue.message);
    }

    throw new EnrollmentValidationError(
      `Validation failed: ${parseResult.error.issues.length} error(s)`,
      parseResult.error,
      fieldErrors
    );
  }

  return parseResult.data;
}

export function useEnrollmentProgress(): EnrollmentProgressState {
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState<EnrollmentValidationError | EnrollmentSubmissionError | null>(null);
  const [lastResult, setLastResult] = useState<ChildApplication | Application | null>(null);

  const updateProgress = useCallback(async (data: unknown): Promise<ChildApplication> => {
    if (!isAmplifyConfigured()) {
      throw new EnrollmentSubmissionError('Cannot update: Bridge not configured', 'NOT_CONFIGURED');
    }

    setUpdating(true);
    setError(null);

    try {
      const validatedInput: UpdateEnrollmentProgressInput = parseInput(
        UpdateEnrollmentProgressInputSchema,
        data
      );
      const result = await updateEnrollmentProgressApi(validatedInput);

      setLastResult(result);
      return result;
    } catch (exception) {
      if (exception instanceof EnrollmentValidationError) {
        setError(exception);
        throw exception;
      }

      const submissionError = new EnrollmentSubmissionError(
        exception instanceof Error ? exception.message : 'Update failed',
        'UPDATE_FAILED',
        exception
      );
      setError(submissionError);
      throw submissionError;
    } finally {
      setUpdating(false);
    }
  }, []);

  const updateMedicalRecord = useCallback(async (data: unknown): Promise<ChildApplication> => {
    if (!isAmplifyConfigured()) {
      throw new EnrollmentSubmissionError('Cannot update: Bridge not configured', 'NOT_CONFIGURED');
    }

    setUpdating(true);
    setError(null);

    try {
      const validatedInput: UpdateMedicalRecordInput = parseInput(
        UpdateMedicalRecordInputSchema,
        data
      );
      const result = await updateMedicalRecordApi(validatedInput);

      setLastResult(result);
      return result;
    } catch (exception) {
      if (exception instanceof EnrollmentValidationError) {
        setError(exception);
        throw exception;
      }

      const submissionError = new EnrollmentSubmissionError(
        exception instanceof Error ? exception.message : 'Update failed',
        'UPDATE_FAILED',
        exception
      );
      setError(submissionError);
      throw submissionError;
    } finally {
      setUpdating(false);
    }
  }, []);

  const upsertStepData = useCallback(async (data: unknown): Promise<ChildApplication> => {
    if (!isAmplifyConfigured()) {
      throw new EnrollmentSubmissionError('Cannot update: Bridge not configured', 'NOT_CONFIGURED');
    }

    setUpdating(true);
    setError(null);

    try {
      const validatedInput: UpsertEnrollmentStepDataInput = parseInput(
        UpsertEnrollmentStepDataInputSchema,
        data
      );
      const result = await upsertEnrollmentStepDataApi(validatedInput);

      setLastResult(result);
      return result;
    } catch (exception) {
      if (exception instanceof EnrollmentValidationError) {
        setError(exception);
        throw exception;
      }

      const submissionError = new EnrollmentSubmissionError(
        exception instanceof Error ? exception.message : 'Update failed',
        'UPDATE_FAILED',
        exception
      );
      setError(submissionError);
      throw submissionError;
    } finally {
      setUpdating(false);
    }
  }, []);

  const completeEnrollment = useCallback(async (data: unknown): Promise<Application> => {
    if (!isAmplifyConfigured()) {
      throw new EnrollmentSubmissionError('Cannot complete: Bridge not configured', 'NOT_CONFIGURED');
    }

    setUpdating(true);
    setError(null);

    try {
      const validatedInput: CompleteEnrollmentInput = parseInput(
        CompleteEnrollmentInputSchema,
        data
      );
      const result = await completeEnrollmentApi(validatedInput);

      setLastResult(result);
      return result;
    } catch (exception) {
      if (exception instanceof EnrollmentValidationError) {
        setError(exception);
        throw exception;
      }

      const submissionError = new EnrollmentSubmissionError(
        exception instanceof Error ? exception.message : 'Completion failed',
        'COMPLETE_FAILED',
        exception
      );
      setError(submissionError);
      throw submissionError;
    } finally {
      setUpdating(false);
    }
  }, []);

  const reset = useCallback(() => {
    setUpdating(false);
    setError(null);
    setLastResult(null);
  }, []);

  return {
    updating,
    error,
    lastResult,
    updateProgress,
    upsertStepData,
    updateMedicalRecord,
    completeEnrollment,
    reset
  };
}
